/**
 * Dashboard schemas — `GET /admin/dashboard/kpis`.
 *
 * Story 4.7a (Visual MVP) defines the contract.
 * Story 4.7b (polish) adds the optional query params (`from`, `to`,
 * `sellerId`) without changing the response shape.
 *
 * `attendanceRate` is `number | null` on purpose. Backend returns `null` when
 * the denominator (`attended + noShow`) is zero — frontend renders "N/A" with
 * an explanatory microcopy. Returning `0` would mislead the manager into
 * reading "everyone missed" when the truth is "no data yet".
 */
import { Type } from '@sinclair/typebox';
import { ConversationStatusSchema, IntentTypeSchema } from './conversations.js';
/** A nullable numeric ratio. `null` means "denominator is zero — no data yet". */
const NullableRatio = Type.Union([Type.Number(), Type.Null()]);
/** ISO-8601 datetime string. */
const Iso = Type.String({ format: 'date-time' });
/**
 * Top-level KPI block. All counts are integers ≥ 0; `attendanceRate` is the
 * only ratio at this level.
 */
const DashboardKpis = Type.Object({
    attendanceRate: NullableRatio,
    meetingsScheduled: Type.Integer({ minimum: 0 }),
    meetingsAttended: Type.Integer({ minimum: 0 }),
    meetingsNoShow: Type.Integer({ minimum: 0 }),
    meetingsCancelled: Type.Integer({ minimum: 0 }),
    /**
     * 2026-08-18 (QA fix, Quinn @qa) — `outcome = 'unknown'`: the attendance
     * callback reminder window expired with no reply from the seller
     * (`attendance-callback-scheduler.worker.ts`'s `casMarkUnknown`), or the
     * seller's reply itself was ambiguous. Same rows backing `unknownMeetings`
     * below ("Reuniões aguardando callback"). Split out from `meetingsScheduled`
     * (`pending`/`confirmed` only) so the admin frontend can sum every
     * `scheduled_at`-in-period meeting EXCEPT `rescheduled` (deliberately
     * excluded — the OLD row of a reschedule, see `queryKpiCounts`) into one
     * "Agendamentos" total without silently undercounting.
     */
    meetingsUnknown: Type.Integer({ minimum: 0 }),
    optOutsTotal: Type.Integer({ minimum: 0 }),
    /**
     * 2026-07-15 — 4 new "resultados da IA" metrics (user request), all
     * computed from signals the AI itself is the exclusive writer of (see
     * `routes/admin/dashboard.ts` for the precise query per field + why each
     * signal was picked over a lookalike that would double-count or include
     * non-AI sources).
     */
    customersContactedByAi: Type.Integer({ minimum: 0 }),
    meetingsConfirmedByAi: Type.Integer({ minimum: 0 }),
    reschedulesTotal: Type.Integer({ minimum: 0 }),
    cancellationsCaughtByAi: Type.Integer({ minimum: 0 }),
    /**
     * 2026-07-16 (user request) — meetings the AI booked from scratch during a
     * lead_new qualification conversation (`meetings.source = 'agent_lead_new'`,
     * the exclusive-writer signal for "the AI made this booking" — see
     * `routes/admin/dashboard.ts`). Deliberately excludes
     * `agent_reschedule`-sourced meetings, already covered by `reschedulesTotal`.
     */
    meetingsBookedByAi: Type.Integer({ minimum: 0 }),
});
/**
 * Per-seller attendance row — used by the bar chart on the dashboard.
 * Empty array when no seller has attended/no-show meetings in the period.
 */
const SellerAttendanceRow = Type.Object({
    sellerId: Type.String({ format: 'uuid' }),
    sellerName: Type.String(),
    attended: Type.Integer({ minimum: 0 }),
    noShow: Type.Integer({ minimum: 0 }),
    attendanceRate: NullableRatio,
});
/**
 * UNKNOWN meetings — those whose attendance callback was sent but the seller
 * has not yet replied. Empty array until Story 4.6 starts populating
 * `meetings.attendance_callback_sent_at`.
 */
const UnknownMeetingRow = Type.Object({
    meetingId: Type.String({ format: 'uuid' }),
    customerName: Type.Union([Type.String(), Type.Null()]),
    scheduledAt: Iso,
    attendanceCallbackSentAt: Type.Union([Iso, Type.Null()]),
    sellerName: Type.String(),
});
/** A nullable average-minutes metric. `null` means no rows reached that stage yet. */
const NullableMinutes = Type.Union([Type.Number(), Type.Null()]);
/**
 * Per-campaign row inside the funnel — lets the dashboard compare e.g.
 * "CashMiles" vs "Comandor + RCC" side by side without a second request.
 */
const LeadQualificationCampaignRow = Type.Object({
    campaignId: Type.String({ format: 'uuid' }),
    campaignName: Type.String(),
    leadsReceived: Type.Integer({ minimum: 0 }),
    qualified: Type.Integer({ minimum: 0 }),
    scheduled: Type.Integer({ minimum: 0 }),
    /** 2026-08-04 (user request) — indicador de no-show em "Desempenho por Campanha". */
    noShow: Type.Integer({ minimum: 0 }),
});
/**
 * "Funil de Qualificação (Lead Novo)" — 2026-07-20 (user request). Answers
 * "how is the lead_new AI funnel doing at qualifying leads and generating
 * meetings for future sales/upgrades" — distinct from the `confirmation`
 * campaign metrics in `DashboardKpis` above (which measure confirmation/
 * reschedule of ALREADY-booked meetings, not lead qualification).
 *
 * Every count is backed by an existing exclusive-writer signal — see
 * `routes/admin/dashboard.ts`'s `queryLeadQualificationFunnel` for the exact
 * query + rationale per field. Deliberately does NOT include "Vendas"/
 * "Faturamento" here (see `SalesIndicator`/`salesIndicator` below instead,
 * added by Story 10.5 — synced from GHL's sales pipeline, not this DB), loss-
 * reason categorization, or AI quality scoring — no backing data exists for
 * the latter two yet.
 */
const LeadQualificationFunnel = Type.Object({
    leadsReceived: Type.Integer({ minimum: 0 }),
    responded: Type.Integer({ minimum: 0 }),
    qualified: Type.Integer({ minimum: 0 }),
    scheduled: Type.Integer({ minimum: 0 }),
    attended: Type.Integer({ minimum: 0 }),
    /**
     * 2026-08-04 (user request, QA fix) — habilita a taxa de comparecimento do
     * funil (`attended / (attended + noShow)`) em vez de `attended / scheduled`,
     * que inflava o denominador com agendamentos futuros/ainda pendentes
     * (ver `LeadQualificationFunnel.tsx` no admin).
     */
    noShow: Type.Integer({ minimum: 0 }),
    avgMinutesToFirstResponse: NullableMinutes,
    avgMinutesToQualified: NullableMinutes,
    avgMinutesToScheduled: NullableMinutes,
    byCampaign: Type.Array(LeadQualificationCampaignRow),
});
/** A nullable rate — `null` means the denominator (previous stage) was zero. */
const NullableRate = Type.Union([Type.Number(), Type.Null()]);
/**
 * Per-template/variant row inside `openingTemplateEffectiveness` — one row
 * per `(campaignId, opening_template_variant)` pair, so an A/B/C test on a
 * single `lead_new` campaign shows up as separate rows for comparison.
 */
const OpeningTemplateEffectivenessRow = Type.Object({
    campaignId: Type.String({ format: 'uuid' }),
    campaignName: Type.String(),
    variantKey: Type.Union([Type.String(), Type.Null()]),
    templateName: Type.Union([Type.String(), Type.Null()]),
    messagesSent: Type.Integer({ minimum: 0 }),
    messagesAnswered: Type.Integer({ minimum: 0 }),
    responseRate: NullableRate,
    meetingsScheduled: Type.Integer({ minimum: 0 }),
    schedulingRate: NullableRate,
});
/**
 * "Efetividade do template de abertura" (2026-07-21, user request) — Mensagens
 * Enviadas/Respondidas/Taxa de Resposta/Reuniões Agendadas/Taxa de
 * Agendamento, globally and per opening-template variant, for `lead_new`
 * campaigns. See `routes/admin/dashboard.ts`'s `queryOpeningTemplateEffectiveness`
 * for the exact query + the "sent" signal rationale.
 */
const OpeningTemplateEffectiveness = Type.Object({
    messagesSent: Type.Integer({ minimum: 0 }),
    messagesAnswered: Type.Integer({ minimum: 0 }),
    responseRate: NullableRate,
    meetingsScheduled: Type.Integer({ minimum: 0 }),
    schedulingRate: NullableRate,
    byTemplate: Type.Array(OpeningTemplateEffectivenessRow),
});
/**
 * "Indicador de vendas" (Story 10.5, 2026-07-28, user request) — contagem +
 * valor total das vendas sincronizadas do estágio "Venda realizada" da
 * pipeline de vendas do GHL, no período/campanha selecionados. `valueSum` é
 * `0` (não `null`) quando não há vendas — mesma convenção de `Type.Integer`
 * acima, mas `Type.Number` porque `sales.value` é `numeric` (pode ter
 * centavos). Ver `routes/admin/dashboard.ts`'s `querySalesIndicator` +
 * `repositories/sales.repo.ts`'s `aggregateInPeriod`.
 *
 * `totalCount`/`totalValueSum` (2026-08-24, user request) — soma bruta de
 * TODA venda sincronizada do estágio, sem exigir cliente/meeting/campanha
 * resolvidos. `count`/`valueSum` continuam medindo só as vendas atribuíveis
 * à IA/campanha (o que o ROI e o health index precisam); vendas de contatos
 * que nunca passaram pelo funil (indicação direta, prospecção externa) só
 * entram em `totalCount`/`totalValueSum`.
 */
const SalesIndicator = Type.Object({
    count: Type.Integer({ minimum: 0 }),
    valueSum: Type.Number({ minimum: 0 }),
    totalCount: Type.Integer({ minimum: 0 }),
    totalValueSum: Type.Number({ minimum: 0 }),
});
/** Per-provider AI cost row — one per `ai.{provider}` audit `resourceType` seen in the period. */
const AiCostByProviderRow = Type.Object({
    provider: Type.String(),
    totalUsd: Type.Number({ minimum: 0 }),
    calls: Type.Integer({ minimum: 0 }),
});
/**
 * "Custos" (Story 10.6, 2026-07-30, user request) — ESTIMATIVA de gasto com
 * OpenAI/Claude/Gemini + WhatsApp API (Meta) no período selecionado. Não é
 * infraestrutura de servidor (decisão de produto, ver Story 10.6 Dev Notes)
 * e não é um valor de billing exato — só data/hora do resto do dashboard,
 * sem filtro de vendedor/campanha (mesma postura de `optOutsTotal`, custo
 * não é atribuível a um vendedor específico). Ver
 * `routes/admin/dashboard.ts`'s `queryCostsSummary`.
 *
 * `whatsapp.messagesSent` conta só envios BEM-SUCEDIDOS instrumentados a
 * partir de quando esta feature entrou no ar — sem backfill (decisão de
 * produto) — períodos anteriores à instrumentação mostram `0`, não o custo
 * real histórico.
 */
const CostsSummary = Type.Object({
    totalUsd: Type.Number({ minimum: 0 }),
    ai: Type.Object({
        totalUsd: Type.Number({ minimum: 0 }),
        byProvider: Type.Array(AiCostByProviderRow),
    }),
    whatsapp: Type.Object({
        totalUsd: Type.Number({ minimum: 0 }),
        messagesSent: Type.Integer({ minimum: 0 }),
    }),
});
/**
 * Story 10.7 (2026-07-30, user request, AC1) — one metric's current-vs-previous
 * comparison. `changePct` is `(current - previous) / previous`, `null` when
 * `previous = 0` (division by zero / "no baseline yet" — same "N/A, not a
 * misleading number" convention as every other nullable ratio in this file).
 */
const PeriodComparisonMetric = Type.Object({
    current: Type.Integer({ minimum: 0 }),
    previous: Type.Integer({ minimum: 0 }),
    changePct: NullableRatio,
});
/**
 * "Comparação de período" (Story 10.7, AC1) — os mesmos 6 números do funil de
 * 6 estágios (`leadsReceived`/`responded`/`qualified`/`scheduled`/`attended`
 * de `LeadQualificationFunnel` + `sales` de `SalesIndicator.count`),
 * comparados contra o período IMEDIATAMENTE anterior de MESMA DURAÇÃO (ex.:
 * mês atual vs. mês anterior; 7 dias vs. os 7 dias antes). Ver
 * `routes/admin/dashboard.ts`'s `queryPeriodComparison`.
 */
const PeriodComparison = Type.Object({
    previousPeriod: Type.Object({
        from: Iso,
        to: Iso,
    }),
    leadsReceived: PeriodComparisonMetric,
    responded: PeriodComparisonMetric,
    qualified: PeriodComparisonMetric,
    scheduled: PeriodComparisonMetric,
    attended: PeriodComparisonMetric,
    sales: PeriodComparisonMetric,
});
/**
 * "Status dos Agendamentos" (Story 10.7, AC3) — contagem de `meetings.outcome`
 * no período/vendedor/campanha selecionados, para o donut. Deliberadamente só
 * 3 fatias (o que a referência do usuário pede) — `pending`/`attended`/
 * `no_show`/`unknown` já aparecem em outros KPIs (`meetingsScheduled`/
 * `meetingsAttended`/`meetingsNoShow` em `DashboardKpis`), não duplicados
 * aqui. Ver `routes/admin/dashboard.ts`'s `queryAppointmentStatusBreakdown`.
 */
const AppointmentStatusBreakdown = Type.Object({
    confirmed: Type.Integer({ minimum: 0 }),
    rescheduled: Type.Integer({ minimum: 0 }),
    cancelled: Type.Integer({ minimum: 0 }),
});
/**
 * "Últimas Interações da IA" (Story 10.7, AC5) — as N conversas mais
 * recentes, para uma lista de atividade recente. Deliberadamente SEM o
 * conteúdo literal da última mensagem do cliente (ver Dev Notes/Riscos da
 * Story 10.7): `status`/`lastIntent` já comunicam "o que está acontecendo"
 * sem expor texto do cliente — mesma convenção de `ConversationListItem`
 * (`admin/conversations`), que também nunca expôs preview de mensagem.
 */
const RecentInteractionRow = Type.Object({
    conversationId: Type.String({ format: 'uuid' }),
    customerName: Type.Union([Type.String(), Type.Null()]),
    campaignName: Type.String(),
    status: ConversationStatusSchema,
    lastIntent: Type.Union([IntentTypeSchema, Type.Null()]),
    lastMessageAt: Type.Union([Iso, Type.Null()]),
    createdAt: Iso,
});
/**
 * "Índice de Saúde Comercial" (Story 10.8, 2026-07-30, user request, AC1) —
 * média aritmética de até 3 taxas (comparecimento/qualificação/conversão em
 * venda, cada uma 0-100), ignorando componentes sem dado. `null` só quando
 * as 3 são `null`. Ver `routes/admin/dashboard.ts`'s
 * `buildCommercialHealthIndex` — função pura, sem query própria, reaproveita
 * `kpis`/`leadQualificationFunnel`/`salesIndicator` já calculados na mesma
 * resposta. Cada taxa é limitada (`clamp`) a no máximo 100 (QA fix, 2ª
 * validação @po): `salesIndicator` filtra por `won_at`, `meetingsAttended`
 * por `scheduled_at` — janelas diferentes podem produzir uma razão > 100%.
 */
const CommercialHealthIndex = Type.Object({
    healthIndex: NullableRatio,
    attendanceRate: NullableRatio,
    qualificationRate: NullableRatio,
    conversionRate: NullableRatio,
});
/**
 * "Qualidade dos Leads" (Story 10.8, 2026-07-30, user request, AC3) —
 * reclassifica os MESMOS leads de `leadQualificationFunnel` em 3 categorias
 * MUTUAMENTE EXCLUSIVAS que somam `leadsReceived`: Alta (qualificado pela
 * IA), Média (respondeu mas não qualificou), Baixa (nunca respondeu). Sem
 * classificação nova da IA — mesmos sinais já usados em
 * `leadQualificationFunnel`, só reagrupados. Ver
 * `routes/admin/dashboard.ts`'s `queryLeadQualityBreakdown`.
 */
const LeadQualityBreakdown = Type.Object({
    alta: Type.Integer({ minimum: 0 }),
    media: Type.Integer({ minimum: 0 }),
    baixa: Type.Integer({ minimum: 0 }),
});
/**
 * "Evolução diária da IA" (Story 10.8, 2026-07-30, user request, AC6) — um
 * ponto por dia (timezone `America/Sao_Paulo`) no período selecionado, 4
 * séries: leads recebidos, qualificados, agendamentos, vendas. `day` é
 * `YYYY-MM-DD` (sem hora — já é a granularidade do gráfico). Ver
 * `routes/admin/dashboard.ts`'s `queryDailyAiEvolution`.
 */
const DailyAiEvolutionRow = Type.Object({
    day: Type.String(),
    leadsReceived: Type.Integer({ minimum: 0 }),
    qualified: Type.Integer({ minimum: 0 }),
    scheduled: Type.Integer({ minimum: 0 }),
    noShow: Type.Integer({ minimum: 0 }),
    sales: Type.Integer({ minimum: 0 }),
});
/**
 * "Prioridades de Hoje" (Story 10.8, 2026-07-30, user request, AC2) — lista
 * fechada de alertas (crítico antes de atenção), a partir de 6 regras fixas
 * avaliadas sobre dado já calculado nesta mesma resposta. Ver
 * `routes/admin/dashboard.ts`'s `buildTodayPriorities` para a lista exata de
 * regras + limiares.
 */
const TodayPriorityAlert = Type.Object({
    tone: Type.Union([Type.Literal('critical'), Type.Literal('watch')]),
    message: Type.String(),
});
/**
 * "Impacto Financeiro / ROI" (Story 10.8, 2026-07-30, user request, AC7) —
 * custo de IA/WhatsApp (`costs.totalUsd`, USD) convertido pra BRL na taxa
 * configurada (`system_settings.usd_to_brl_rate`). `custoPorAgendamentoBRL`
 * usa `kpis.meetingsScheduled` (cross-campanha, mesmo escopo do numerador —
 * QA fix, 2ª validação @po). Estimativa, não conciliação financeira exata
 * (mesmo espírito de `CostsSummary`).
 */
const CommercialRoi = Type.Object({
    leadsGerados: Type.Integer({ minimum: 0 }),
    custoEstimadoBRL: Type.Number({ minimum: 0 }),
    custoPorAgendamentoBRL: NullableRatio,
    roiMultiplier: NullableRatio,
    usdToBrlRate: Type.Number({ minimum: 0 }),
});
/**
 * "Motivos de Perda" (Story 10.8, 2026-07-30, user request, AC4) — contagem
 * por motivo, sincronizado do custom field "Motivo" de opportunities
 * `status='lost'` no GHL. Lista vazia quando a sincronização nunca rodou (ou
 * nenhum motivo caiu no período) — estado vazio, não erro. Ver
 * `routes/admin/dashboard.ts`'s `queryLossReasonBreakdown`.
 */
const LossReasonRow = Type.Object({
    reason: Type.String(),
    count: Type.Integer({ minimum: 0 }),
});
/**
 * "Insight Principal" (Story 10.8, 2026-07-30, user request, AC5) — texto
 * curto gerado por LLM, cacheado e regenerado no máximo a cada 3h por
 * `dashboard-insight-scheduler.worker.ts`. `null` até o primeiro tick rodar.
 * SEMPRE sobre "mês corrente até agora" (`periodFrom`/`periodTo`),
 * independente do período selecionado nos filtros do dashboard — a UI
 * precisa rotular isso explicitamente (ver Story 10.8 AC5 Dev Notes).
 * Redigido (null) para a sessão `vendedor`, mesmo espírito de
 * `openingTemplateEffectiveness`/`costs` — agregado da empresa inteira, sem
 * recorte por vendedor.
 */
const CommercialInsight = Type.Object({
    text: Type.String(),
    generatedAt: Iso,
    periodFrom: Iso,
    periodTo: Iso,
});
/**
 * Full response shape. `period.from` / `period.to` always reflect the actual
 * window queried — month-to-date in the 4.7a MVP, configurable in 4.7b.
 */
export const DashboardKpisResponse = Type.Object({
    period: Type.Object({
        from: Iso,
        to: Iso,
    }),
    kpis: DashboardKpis,
    bySellerAttendance: Type.Array(SellerAttendanceRow),
    unknownMeetings: Type.Array(UnknownMeetingRow),
    leadQualificationFunnel: LeadQualificationFunnel,
    openingTemplateEffectiveness: OpeningTemplateEffectiveness,
    salesIndicator: SalesIndicator,
    costs: CostsSummary,
    commercialHealthIndex: CommercialHealthIndex,
    leadQualityBreakdown: LeadQualityBreakdown,
    dailyAiEvolution: Type.Array(DailyAiEvolutionRow),
    todayPriorities: Type.Array(TodayPriorityAlert),
    commercialRoi: CommercialRoi,
    periodComparison: PeriodComparison,
    appointmentStatusBreakdown: AppointmentStatusBreakdown,
    recentInteractions: Type.Array(RecentInteractionRow),
    lossReasonBreakdown: Type.Array(LossReasonRow),
    commercialInsight: Type.Union([CommercialInsight, Type.Null()]),
});
/**
 * Query params for `GET /admin/dashboard/kpis` (Story 4.7b).
 *
 * All optional — when absent, the route falls back to the 4.7a behaviour
 * (month-to-date, all sellers). When `from`/`to` are partial (only one
 * provided), the missing side defaults to the same month-to-date anchor.
 */
export const DashboardKpisQuery = Type.Object({
    from: Type.Optional(Iso),
    to: Type.Optional(Iso),
    sellerId: Type.Optional(Type.String({ format: 'uuid' })),
    /**
     * Story 12.7 (Epic E12) — scope every KPI to a single SDR's carteira
     * (`customers.assigned_sdr_id`). Optional for `admin`/`manager` (same
     * spirit as `sellerId`); a `role='sdr'` session ignores this and is
     * force-clamped to its own id server-side — see `routes/admin/dashboard.ts`.
     */
    sdrId: Type.Optional(Type.String({ format: 'uuid' })),
    /**
     * 2026-07-16 (user request) — scope every KPI to a single campaign. Absent
     * means "all campaigns" (existing behaviour, unchanged).
     */
    campaignId: Type.Optional(Type.String({ format: 'uuid' })),
});
//# sourceMappingURL=dashboard.js.map